import type { ReactElement } from 'react';
import { Users } from 'lucide-react';
import type {
  BrowserAiToolListItem,
  BrowserAiNavigationType,
} from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import { BROWSER_AI_NAVIGATION_TYPE_COUNT_KEYS } from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import { getShadowAiCategoryLabel } from '@kirin/api-client/contracts/shadow-ai';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TREND_CONFIG } from './trendConfig';

export function DomainCell({
  row,
}: {
  row: BrowserAiToolListItem;
}): ReactElement {
  // Plain text (KIR-3813): clicking the domain must not navigate off-app.
  // The click bubbles to the row, expanding it (see DataTable row expansion).
  return (
    <span className="text-xs font-mono text-muted-foreground">
      {row.domain}
    </span>
  );
}

export function NameCell({
  row,
}: {
  row: BrowserAiToolListItem;
}): ReactElement {
  return <span className="text-sm">{row.name || '—'}</span>;
}

export function CategoryCell({
  row,
}: {
  row: BrowserAiToolListItem;
}): ReactElement {
  const label = getShadowAiCategoryLabel(row.category);
  return (
    <Badge variant="secondary" className="text-xs">
      {label}
    </Badge>
  );
}

export function TrendCell({
  row,
}: {
  row: BrowserAiToolListItem;
}): ReactElement {
  if (!row.trend) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  const config = TREND_CONFIG[row.trend] ?? { label: row.trend, className: '' };
  return (
    <span
      className={cn(
        'text-xs font-medium flex items-center gap-1',
        config.className
      )}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

export function UsersCell({
  row,
}: {
  row: BrowserAiToolListItem;
}): ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5 font-medium text-muted-foreground">
      <Users className="h-3.5 w-3.5" />
      {row.userCount}
    </span>
  );
}

/** True when the row carries per-bucket counts (i.e. from a KIR-3890 agent). */
function hasBucketCounts(row: BrowserAiToolListItem): boolean {
  return (
    (row.realCount ?? 0) +
      (row.redirectCount ?? 0) +
      (row.addressBarCount ?? 0) +
      (row.adsTrackingCount ?? 0) >
    0
  );
}

export function VisitsCell({
  row,
  navigationType,
}: {
  row: BrowserAiToolListItem;
  /**
   * When a "how it was reached" filter is active AND the row has per-bucket
   * counts, the cell shows that bucket's visit count instead of the global
   * total. Legacy rows (no per-bucket data) fall back to the global count.
   */
  navigationType?: BrowserAiNavigationType;
}): ReactElement {
  // Contextual mode: when a navigation-type filter is active and the row has
  // per-bucket data, show that bucket's count; otherwise the global total. Number
  // only — the active filter / drill-down already tells the operator the context.
  const count =
    navigationType && hasBucketCounts(row)
      ? (row[BROWSER_AI_NAVIGATION_TYPE_COUNT_KEYS[navigationType]] ?? 0)
      : row.visitCount;
  return <span className="font-medium text-muted-foreground">{count}</span>;
}

export function FirstDetectedCell({
  row,
}: {
  row: BrowserAiToolListItem;
}): ReactElement {
  if (!row.firstDetected) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  return <RelativeTimeWithTooltip date={row.firstDetected} />;
}

export function LastSeenCell({
  row,
}: {
  row: BrowserAiToolListItem;
}): ReactElement {
  if (!row.lastSeen) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  return <RelativeTimeWithTooltip date={row.lastSeen} />;
}
